import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { FormatChips } from "@/components/silver/builder/BuilderOptions";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { RollingNumber } from "@/components/silver/common/RollingNumber";
import { areasText } from "@/components/silver/results/AudienceHeadline";
import { BASE_ADDRESS_PRICE, formatGBP } from "@/config/pricing";

const EASE = [0.22, 1, 0.36, 1];
const R = 40;
const C = 2 * Math.PI * R;

function ShareRing({ value }) {
  return (
    <div className="relative h-[100px] w-[100px] shrink-0" data-testid="audience-share-ring">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
        <motion.circle cx="50" cy="50" r={R} fill="none" stroke="url(#share-ring)" strokeWidth="8" strokeLinecap="round" strokeDasharray={C}
          initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - Math.max(0.02, value)) }} transition={{ duration: 1.4, ease: EASE, delay: 0.3 }} />
        <defs>
          <linearGradient id="share-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#7AA818" />
            <stop offset="1" stopColor="#C6EE5A" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 grid place-items-center num text-[18px] font-extrabold text-white">{Math.round(value * 100)}%</span>
    </div>
  );
}

function PriceTile() {
  const { audience, price } = useSilver();
  return (
    <motion.div className="relative flex flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-[#A6D32A] to-[#8DB81C] p-5 text-[#12290F] shadow-[0_24px_60px_-20px_rgba(150,193,31,0.75)]"
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: EASE, delay: 0.25 }} data-testid="campaign-price-tile">
      <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-white/25 blur-2xl" aria-hidden="true" />
      <p className="font-ui text-[10px] font-bold uppercase tracking-[0.16em] text-[#12290F]/70">Estimated campaign</p>
      <p className="mt-2 num text-[40px] font-extrabold leading-none tracking-[-0.045em] sm:text-[46px]">
        <RollingNumber value={price.baseCost} format={formatGBP} data-testid="campaign-price-total" />
      </p>
      <p className="mt-3 text-[12.5px] font-semibold text-[#12290F]/80">
        {formatGBP(BASE_ADDRESS_PRICE)} per address · {audience.total.toLocaleString("en-GB")} addresses
      </p>
      <p className="mt-auto pt-3 text-[11px] text-[#12290F]/60">Indicative. Confirmed by the Print team.</p>
    </motion.div>
  );
}

// The audience you're about to reach, what it costs, the format, and the one action that finishes the job.
export function AudiencePanel() {
  const { audience, audienceLabel, setBuilderMode } = useSilver();
  const share = audience.silverTotal ? audience.total / audience.silverTotal : 0;
  return (
    <motion.div key="build" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -40, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="p-6 sm:p-8" data-testid="audience-panel">
      <p className="eyebrow">Your audience</p>
      <div className="mt-5 grid gap-5 sm:grid-cols-2 sm:items-stretch">
        <div className="flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5">
          <ShareRing value={share} />
          <div className="min-w-0">
            <p className="font-ui text-[10px] font-bold uppercase tracking-[0.16em] text-white/55">Households to reach</p>
            <p className="mt-2 num text-[40px] font-extrabold leading-none tracking-[-0.05em] text-white sm:text-[46px]">
              <AnimatedNumber value={audience.total} duration={1.2} data-testid="campaign-audience-total" />
            </p>
            <p className="mt-3 text-[12.5px] leading-snug text-white/60">
              <span className="text-white/85">{audienceLabel}</span> · <span className="font-semibold text-white">{areasText(audience.areas)}</span>
            </p>
            <p className="mt-1 text-[11px] text-white/45">{Math.round(share * 100)}% of homeowner households in your patch</p>
          </div>
        </div>
        <PriceTile />
      </div>

      <div className="my-7 h-px bg-white/10" aria-hidden="true" />
      <FormatChips />

      <button type="button" className="btn-primary mt-7 w-full !py-4 text-[16px]" disabled={!audience.total} onClick={() => setBuilderMode("enquire")} data-testid="enquire-btn">
        Enquire <ArrowRight className="h-5 w-5" />
      </button>
      <p className="mt-3 text-center text-[11px] text-white/35">No commitment — the Print team confirms specification and pricing with you.</p>
    </motion.div>
  );
}
